import Alpine from "alpinejs";

const TOKEN_KEY = "paperclip_chat_jwt";

function authHeaders() {
  const _t = sessionStorage.getItem(TOKEN_KEY);
  if (!_t) {
    return {};
  }
  return { Authorization: `Bearer ${_t}` };
}

async function apiJson(path, options = {}) {
  const _res = await fetch(path, {
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...authHeaders(),
      ...options.headers,
    },
  });
  const _text = await _res.text();
  let _data = null;
  if (_text) {
    try {
      _data = JSON.parse(_text);
    } catch {
      _data = { raw: _text };
    }
  }
  if (!_res.ok) {
    const _err = new Error(_data?.error ?? `http_${_res.status}`);
    _err.status = _res.status;
    _err.body = _data;
    throw _err;
  }
  return _data;
}

Alpine.data("chatApp", () => ({
  password: "",
  error: "",
  token: null,
  companyId: null,
  agents: [],
  issues: [],
  selectedIssueId: null,
  comments: [],
  messageDraft: "",
  pollTimer: null,

  init() {
    const _existing = sessionStorage.getItem(TOKEN_KEY);
    if (_existing) {
      this.token = _existing;
      this.bootstrap();
    }
  },

  async login() {
    this.error = "";
    try {
      const _data = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ password: this.password }),
      }).then(async (r) => {
        const _j = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error(_j.error ?? "login_failed");
        }
        return _j;
      });
      this.token = _data.token;
      sessionStorage.setItem(TOKEN_KEY, this.token);
      this.password = "";
      await this.bootstrap();
    } catch (_e) {
      this.error = String(_e.message ?? _e);
    }
  },

  logout() {
    sessionStorage.removeItem(TOKEN_KEY);
    this.token = null;
    this.companyId = null;
    this.agents = [];
    this.issues = [];
    this.selectedIssueId = null;
    this.comments = [];
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  },

  async bootstrap() {
    const _me = await apiJson("/api/upstream/agents/me");
    this.companyId = _me.companyId;
    await this.refreshSidebar();
    this.startPoll();
  },

  async refreshSidebar() {
    if (!this.companyId) {
      return;
    }
    const [_agents, _issues] = await Promise.all([
      apiJson(`/api/upstream/companies/${this.companyId}/agents`),
      apiJson(
        `/api/upstream/companies/${this.companyId}/issues?status=todo,in_progress,blocked&limit=50`,
      ),
    ]);
    this.agents = Array.isArray(_agents) ? _agents : [];
    const _list = Array.isArray(_issues) ? _issues : _issues?.items ?? [];
    this.issues = _list;
  },

  startPoll() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
    this.pollTimer = setInterval(async () => {
      try {
        await this.refreshSidebar();
        if (this.selectedIssueId) {
          await this.loadComments(this.selectedIssueId, false);
        }
      } catch (_e) {
        console.warn("poll_failed", _e);
      }
    }, 5000);
  },

  async selectIssue(issueId) {
    this.selectedIssueId = issueId;
    await this.loadComments(issueId, true);
  },

  async loadComments(issueId, scrollBottom) {
    const _rows = await apiJson(
      `/api/upstream/issues/${issueId}/comments?order=asc`,
    );
    this.comments = Array.isArray(_rows) ? _rows : [];
    if (scrollBottom) {
      this.$nextTick(() => {
        const _el = document.getElementById("thread-end");
        if (_el) {
          _el.scrollIntoView({ behavior: "smooth" });
        }
      });
    }
  },

  async sendMessage() {
    if (!this.selectedIssueId || !this.messageDraft.trim()) {
      return;
    }
    const _body = this.messageDraft.trim();
    this.messageDraft = "";
    await apiJson(`/api/upstream/issues/${this.selectedIssueId}/comments`, {
      method: "POST",
      body: JSON.stringify({ body: _body }),
    });
    await this.loadComments(this.selectedIssueId, true);
  },
}));

Alpine.start();
